import React, { useCallback, useEffect, useState } from 'react'

import { connect } from 'react-redux'
import { values } from 'lodash'
import { Button, DatePicker, Divider, message } from 'antd'
import FormItem from 'antd/es/form/FormItem'
import { faCalendar, faCheck, faClinicMedical, faGraduationCap, faSchool, faStethoscope, faUserGraduate } from '@fortawesome/pro-solid-svg-icons'

import IconLabel, { FontSizes } from '../../../Components/icons/IconLabel'
import SchoolsSelector from '../../../Components/shared/Selectors/School/SchoolsSelector'
import InstitutionsSelector from '../../../Components/shared/Selectors/Institution/InstitutionsSelector'
import SectorSelector from '../../../Components/shared/Selectors/SectorSelector'
import { isValid } from '../../../utils/validators'
import { EUROPEAN_DATE } from '../../../utils/constants'
import SchoolSectionSelector from '../../../Components/shared/Selectors/SchoolSectionSelector'
import { isSchoolUser } from '../../../utils/roles'
import { selectUser } from '../../../reducers/UserReducer'
import IconButton from '../../../Components/antd/Buttons/IconButton'
import { canUserEditProperty, Internship, InternshipProperties } from '../../../utils/entities/internship'
import { getSchoolSchoolSections } from '../../../utils/api/school'
import { getSchoolSectionSectionStudents } from '../../../utils/api/schoolSection'
import StudentsSelector from '../../../Components/shared/Selectors/Student/StudentsSelector'
import { getInstitutionSectors } from '../../../utils/api/institution'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { getIdOrNull, isObject } from '../../../utils'

const COLUMN_WIDTH = '350px'
const FIELD_WIDTH = '200px'
const { START_DATE, END_DATE, SCHOOL, SECTION, INSTITUTION, SECTOR, STUDENT } = InternshipProperties

message.config({
  top: 20,
  duration: 3,
  maxCount: 1,
  placement: 'topRight'
})

const mapStateToProps = state => ({ user: selectUser(state), t: selectTranslate(state) })

const AutonomousInternshipForm = ({ data, sectors, user, validators, onSubmit, onCancel, t }) => {
  const [error, setError] = useState({ missingFields: [] })
  const [localData, setLocalData] = useState(data)
  const [sections, setSections] = useState({ data: [], loading: false })
  const [students, setStudents] = useState({ data: [], loading: false })
  const [localSectors, setLocalSectors] = useState({ data: [], loading: false })

  const isSchoolSide = isSchoolUser(user)

  useEffect(() => {
    if (localData?.school) {
      setSections({ data: [], loading: true })
      getSchoolSchoolSections(user, { id: getIdOrNull(localData.school) }).then(({ data }) => {
        const sections = data[getIdOrNull(localData.school)]

        setSections({ data: sections.filter(s => !!s.sectionToYear), loading: false })
      })
    }
  }, [user, localData?.school])

  useEffect(() => {
    if (localData?.section && isSchoolSide) {
      setStudents({ data: [], loading: true })
      getSchoolSectionSectionStudents(user, { id: localData.section.id }).then(({ data }) => {
        setStudents({ data, loading: false })
      })
    }
  }, [user, localData?.section])

  useEffect(() => {
    if (localData?.institution && isSchoolSide) {
      setLocalSectors({ data: [], loading: true })
      getInstitutionSectors(user, { id: localData.institution.id }).then(({ data }) => {
        setLocalSectors({ data, loading: false })
      })
    }
  }, [user, localData?.institution])

  const handleSubmit = (e) => {
    const errors = isValid(localData, values(validators))

    if (localData.startDate > localData.endDate) {
      message.warning(t('internship_form.error_dates'))
    } else if (errors.keys.length === 0) {
      onSubmit(new Internship(localData))
    }

    setError({
      ...error,
      title: t('internship_form.error_field'),
      visible: true,
      missingFields: errors.keys
    })
  }

  const getStyle = key => {
    const style = { width: FIELD_WIDTH }

    if (error.missingFields.includes(key)) {
      style.border = '2px solid red'
      style.borderRadius = '4px'
    }

    return style
  }

  const isDisabled = useCallback(property => !canUserEditProperty(user, localData, property), [user, localData])

  return (
    <div className='flex-column'>
      <div className='form-class' style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start' }}>
        {/* Colonne de gauche */}
        <div style={{ width: COLUMN_WIDTH }}>
          <FormItem label={<IconLabel icon={faCalendar} text={t('common.start_date')} fontSize={FontSizes.SMALL} padding='0px' />}>
            <DatePicker
              value={localData.startDate}
              onChange={startDate => setLocalData({ ...localData, startDate })}
              format={EUROPEAN_DATE}
              style={getStyle(START_DATE)}
              disabled={isDisabled(START_DATE)}
            />
          </FormItem>
          <FormItem label={<IconLabel icon={faSchool} text={t('common.school')} fontSize={FontSizes.SMALL} padding='0px' />}>
            <SchoolsSelector
              multiple='default'
              values={isObject(localData.school) && localData.school.name ? localData.school.name : getIdOrNull(localData.school)}
              onChange={school => setLocalData({ ...localData, school, section: null })}
              style={getStyle(SCHOOL)}
              disabled={isDisabled(SCHOOL)}
            />
          </FormItem>
          <FormItem label={<IconLabel icon={faGraduationCap} text={t('common.section_and_year')} fontSize={FontSizes.SMALL} padding='0px' />}>
            <SchoolSectionSelector
              disabled={sections.data.length === 0 || sections.loading || isDisabled(SECTION)}
              schoolSections={sections.data}
              values={getIdOrNull(localData.section)}
              onChange={(section, _, entities) => {
                const selectedEntity = Array.isArray(entities) ? entities[0] : entities
                setLocalData(prev => ({
                  ...prev,
                  section,
                  internshipYear: selectedEntity?.sectionToYear ?? null
                }))
              }}
              style={getStyle(SECTION)}
              loading={sections.loading}
            />
          </FormItem>
          {isSchoolSide && (
            <FormItem label={<IconLabel icon={faUserGraduate} text={t('common.student')} fontSize={FontSizes.SMALL} padding='0px' />}>
              <StudentsSelector
                disabled={students.data.length === 0 || students.loading || isDisabled(STUDENT)}
                students={students.data}
                values={getIdOrNull(localData.student)}
                onChange={student => setLocalData({ ...localData, student })}
                style={getStyle(STUDENT)}
                loading={students.loading}
              />
            </FormItem>
          )}
        </div>

        {/* Barre verticale */}
        <div
          style={{
            width: '1px',
            background: 'linear-gradient(to bottom, transparent, #d9d9d9, transparent)',
            alignSelf: 'stretch',
            margin: '0 8px'
          }}
        />

        {/* Colonne de droite */}
        <div style={{ width: COLUMN_WIDTH }}>
          <FormItem label={<IconLabel icon={faCalendar} text={t('common.end_date')} fontSize={FontSizes.SMALL} padding='0px' />}>
            <DatePicker
              value={localData.endDate}
              onChange={endDate => setLocalData({ ...localData, endDate })}
              format={EUROPEAN_DATE}
              style={getStyle(END_DATE)}
              disabled={isDisabled(END_DATE)}
            />
          </FormItem>
          <FormItem label={<IconLabel icon={faClinicMedical} text={t('common.institution')} fontSize={FontSizes.SMALL} padding='0px' />}>
            <InstitutionsSelector
              multiple='default'
              values={localData.institution.name ? localData.institution.name : getIdOrNull(localData.institution)}
              onChange={institution => setLocalData({ ...localData, institution, sector: null })}
              disabled={isDisabled(INSTITUTION)}
              style={getStyle(INSTITUTION)}
            />
          </FormItem>
          <FormItem label={<IconLabel icon={faStethoscope} text={t('common.sector')} fontSize={FontSizes.SMALL} padding='0px' />}>
            <SectorSelector
              disabled={!localData.institution || isDisabled(SECTOR)}
              sectors={isSchoolSide ? localSectors.data : sectors}
              values={getIdOrNull(localData.sector)}
              onChange={sector => setLocalData({ ...localData, sector })}
              style={getStyle(SECTOR)}
            />
          </FormItem>
        </div>
      </div>
      <div className='modal-footer'>
        <Divider />
        <div className='form-footer flex-row aln-ctr jst-end'>
          <Button key='cancel' type='default' onClick={onCancel}> {t('common.cancel')} </Button>
          <IconButton key='save' type='primary' onClick={handleSubmit} icon={faCheck} text={t('common.save')} />
        </div>
      </div>
    </div>
  )
}

export default connect(mapStateToProps)(AutonomousInternshipForm)
